import { Drawer, Text, UnstyledButton } from "@mantine/core";
import { Badge, Spinner } from "@onengine/component-library";
import { useNavigate } from "@tanstack/react-router";
import type { FormVersionSummary } from "@/api/form-builder/formBuilder.types";
import { useFormVersions } from "@/hooks/form-builder/useFormVersions";
import { formatDateTime } from "@/utils/dateFormatter";
import styles from "./FormVersionHistoryDrawer.module.scss";

const DRAWER_SIZE = 420;
const DRAWER_CLASS_NAMES = { body: styles.drawerBody };

const byNewestVersion = (a: FormVersionSummary, b: FormVersionSummary) => b.version - a.version;

interface FormVersionHistoryDrawerProps {
  formId: string;
  formName: string;
  tenantId?: string;
  opened: boolean;
  onClose: () => void;
}

export function FormVersionHistoryDrawer({
  formId,
  formName,
  tenantId,
  opened,
  onClose,
}: FormVersionHistoryDrawerProps) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useFormVersions(opened ? formId : undefined, tenantId);
  const versions = [...(data ?? [])].sort(byNewestVersion);

  const openVersion = (version: number) => {
    onClose();
    navigate({ to: "/form-builder/editor", search: { formId, tenantId, version } });
  };

  const renderContent = () => {
    if (isLoading) return <Spinner size="small" />;
    if (isError)
      return (
        <Text size="sm" c="dimmed">
          {"Couldn't load versions"}
        </Text>
      );
    if (versions.length === 0)
      return (
        <Text size="sm" c="dimmed">
          No published versions yet
        </Text>
      );
    return versions.map((item) => (
      <UnstyledButton
        key={item.version}
        className={styles.versionCard}
        aria-label={`Open v${item.version}`}
        onClick={() => openVersion(item.version)}
      >
        <div className={styles.versionTitle}>
          <span className={styles.versionName}>v{item.version}</span>
          {item.isCurrent && (
            <Badge color="info" variant="lowContrast" size="small">
              Current
            </Badge>
          )}
        </div>
        <div className={styles.versionMeta}>
          <span>{formatDateTime(item.publishedAt, true, true, true)}</span>
          {item.publishedBy?.name && (
            <>
              <span className={styles.dot}>•</span>
              <span>{item.publishedBy.name}</span>
            </>
          )}
        </div>
      </UnstyledButton>
    ));
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={DRAWER_SIZE}
      padding={0}
      title={<span className={styles.title}>Version history · {formName}</span>}
      classNames={DRAWER_CLASS_NAMES}
    >
      <div className={styles.body}>{renderContent()}</div>
    </Drawer>
  );
}
